import React from 'react';

// Helper functions for transportation guidelines
const getTransportClassDescription = (transportClass) => {
  const descriptions = {
    '1': 'Explosives',
    '2': 'Gases (compressed, liquefied, or dissolved under pressure)',
    '3': 'Flammable liquids',
    '4': 'Flammable solids, spontaneously combustible, dangerous when wet',
    '5': 'Oxidizing substances and organic peroxides',
    '6': 'Toxic and infectious substances',
    '7': 'Radioactive materials',
    '8': 'Corrosive substances',
    '9': 'Miscellaneous dangerous goods'
  };
  return descriptions[transportClass] || 'Classification varies by specific hazard';
};

const getPackingGroupDescription = (packingGroup) => {
  const descriptions = {
    'I': 'High danger - Substances presenting high danger',
    'II': 'Medium danger - Substances presenting medium danger',
    'III': 'Low danger - Substances presenting low danger'
  };
  return descriptions[packingGroup] || 'Danger level classification for transport';
};

const getTransportationGuidelines = (transportation) => {
  const guidelines = [];
  
  if (!transportation) return ['Follow general hazardous material transport regulations'];

  // Guidelines based on transport class
  if (transportation.transport_class) {
    const classGuidelines = {
      '1': [
        'Keep away from heat, sparks, and flames',
        'Use explosion-proof equipment',
        'Avoid shock and friction'
      ],
      '2': [
        'Ensure proper ventilation during transport',
        'Check pressure vessel integrity',
        'Use appropriate gas-tight containers'
      ],
      '3': [
        'Keep away from ignition sources',
        'Use grounded equipment',
        'Ensure proper ventilation'
      ],
      '4': [
        'Keep dry and away from water',
        'Store in original containers',
        'Avoid heat and ignition sources'
      ],
      '5': [
        'Keep away from combustible materials',
        'Store in cool, dry conditions',
        'Use non-metallic containers when specified'
      ],
      '6': [
        'Use appropriate protective equipment',
        'Ensure proper labeling and documentation',
        'Follow biohazard protocols if applicable'
      ],
      '7': [
        'Follow radiation safety protocols',
        'Use appropriate shielding',
        'Monitor radiation levels'
      ],
      '8': [
        'Use corrosion-resistant containers',
        'Avoid contact with incompatible materials',
        'Ensure proper ventilation'
      ],
      '9': [
        'Follow specific handling instructions',
        'Check material-specific requirements',
        'Use appropriate containment'
      ]
    };
    
    if (classGuidelines[transportation.transport_class]) {
      guidelines.push(...classGuidelines[transportation.transport_class]);
    }
  }

  // Guidelines based on packing group
  if (transportation.packing_group) {
    const packingGuidelines = {
      'I': [
        'Use highest level containment and safety measures',
        'Require special permits and documentation',
        'Follow strictest transport regulations'
      ],
      'II': [
        'Use standard safety precautions',
        'Follow regular hazmat transport protocols',
        'Ensure proper labeling and placarding'
      ],
      'III': [
        'Follow basic safety measures',
        'Use standard transport containers',
        'Maintain proper documentation'
      ]
    };
    
    if (packingGuidelines[transportation.packing_group]) {
      guidelines.push(...packingGuidelines[transportation.packing_group]);
    }
  }

  // General guidelines
  guidelines.push(
    'Ensure driver has proper hazmat certification',
    'Carry appropriate emergency response information',
    'Follow DOT/international transport regulations',
    'Inspect packaging before transport'
  );

  return [...new Set(guidelines)]; // Remove duplicates
};

const TransportationSection = ({ chemicalDetails }) => {
  return (
    <div className="bg-white rounded-2xl shadow-lg p-8 border border-gray-100 transition-all duration-300 hover:shadow-xl">
      <h2 className="text-2xl font-bold mb-6">
        <i className="fas fa-truck mr-2"></i>Transportation Guidelines
      </h2>
      <div className="space-y-6">
        {/* Use new backend structure: chemicalDetails.transportation_guidelines */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* UN Number */}
          {chemicalDetails.transportation_guidelines?.un_number && (
            <div className="p-6 bg-blue-50 border border-blue-200 rounded-lg">
              <h3 className="text-lg font-bold text-blue-800 mb-2">
                <i className="fas fa-barcode mr-2"></i>UN Number
              </h3>
              <div className="text-2xl font-bold text-blue-900 mb-2">
                {chemicalDetails.transportation_guidelines.un_number}
              </div>
              <div className="text-sm text-blue-700">
                United Nations identification number for hazardous materials transport
              </div>
            </div>
          )}
        </div>
        {/* Key Transportation Guidelines */}
        <div className="bg-gray-50 rounded-lg p-6">
          <h3 className="text-lg font-bold mb-4 text-gray-800">
            <i className="fas fa-clipboard-list mr-2"></i>Key Transportation Guidelines
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {getTransportationGuidelines(chemicalDetails.transportation_guidelines).map((guideline, index) => (
              <div key={index} className="flex items-start space-x-3 p-3 bg-white rounded border">
                <i className="fas fa-check-circle text-green-500 mt-1"></i>
                <span className="text-gray-700">{guideline}</span>
              </div>
            ))}
          </div>
        </div>
        {/* No transportation info */}
        {!chemicalDetails.transportation_guidelines && (
          <div className="p-12 bg-gray-100 rounded-lg hover:bg-slate-300 transition-all">
            <div className="text-center">No transportation information available.</div>
          </div>
        )}
      </div>
    </div>
  );
};

export { getTransportClassDescription, getPackingGroupDescription, getTransportationGuidelines };
export default TransportationSection;
